"use client";

import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Play, CreditCard } from "lucide-react";
import { useTour } from "./TourProvider";
import { useOnboardingMode, stageHref, gexStageHref } from "@/hooks/use-onboarding-mode";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";

interface ExpiredSubscriptionGateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExpiredSubscriptionGate({ open, onOpenChange }: ExpiredSubscriptionGateProps) {
  const { startWheelOnboarding, startGexOnboarding } = useTour();
  const router = useRouter();
  const { data: session } = useSession();
  const storageScope = session?.user?.id || 'anon';
  const allowLegacyState = storageScope === 'anon';
  const wheelStateKey = `qw_wheel_onboarding_state:${storageScope}`;
  const gexStateKey = `qw_gex_onboarding_state:${storageScope}`;
  const [showPricing, setShowPricing] = useState(false);

  const hasSavedWheel = typeof window !== 'undefined' && (() => {
    try {
      const r = localStorage.getItem(wheelStateKey) || (allowLegacyState ? localStorage.getItem('qw_wheel_onboarding_state') : null);
      return r ? JSON.parse(r).active : false;
    } catch { return false; }
  })();
  const hasSavedGex = typeof window !== 'undefined' && (() => {
    try {
      const r = localStorage.getItem(gexStateKey) || (allowLegacyState ? localStorage.getItem('qw_gex_onboarding_state') : null);
      return r ? JSON.parse(r).active : false;
    } catch { return false; }
  })();
  const hasSavedState = hasSavedWheel || hasSavedGex;

  const handleResumeTour = () => {
    onOpenChange(false);
    if (hasSavedWheel) {
      try {
        const raw = localStorage.getItem(wheelStateKey) || (allowLegacyState ? localStorage.getItem('qw_wheel_onboarding_state') : null);
        const saved = raw ? JSON.parse(raw) : null;
        if (!saved) throw new Error('No saved wheel state');
        router.push(stageHref(saved.stage));
      } catch {
        startWheelOnboarding('wheel', true);
      }
    } else if (hasSavedGex) {
      try {
        const raw = localStorage.getItem(gexStateKey) || (allowLegacyState ? localStorage.getItem('qw_gex_onboarding_state') : null);
        const saved = raw ? JSON.parse(raw) : null;
        if (!saved) throw new Error('No saved gex state');
        router.push(gexStageHref(saved.stage));
      } catch {
        startGexOnboarding();
      }
    } else {
      startWheelOnboarding('wheel', true);
    }
  };

  const handleRenew = () => {
    onOpenChange(false);
    window.location.href = "/pricing?plan=pro";
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-lg p-0 gap-0 overflow-visible border-0 bg-transparent shadow-none [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="flex justify-center p-4">
          <div className="w-full max-w-lg rounded-lg overflow-hidden shadow-xl">
            <div className="h-1.5 rounded-t-lg bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-600 to-transparent" />
            <div className="rounded-b-lg border border-t-0 border-border/50 bg-background shadow-lg">
              <div className="p-8 space-y-6">
                <div className="space-y-2 text-center">
                  <h2 className="text-xl font-semibold tracking-tight">
                    Your subscription has expired
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Welcome back! Renew to continue using all features, or take a
                    quick refresher tour to see what&apos;s new.
                  </p>
                </div>

                <div className="space-y-3">
                  <Button
                    size="lg"
                    className="w-full h-12 bg-foreground text-background hover:bg-foreground/90"
                    onClick={handleRenew}
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    Renew Subscription
                  </Button>

                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full h-12"
                    onClick={handleResumeTour}
                  >
                    <Play className="mr-2 h-4 w-4" />
                    {hasSavedState ? "Resume Tour" : "Quick Tour"}
                  </Button>
                </div>

                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-sm font-medium">$1 for 7 days</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Cancel anytime &middot; 14-day money-back guarantee
                  </p>
                </div>

                <p className="text-center text-xs text-muted-foreground">
                  Need to manage or delete your account?{" "}
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:text-foreground"
                    // Don't close the gate here: TourProvider hides it on
                    // /dashboard/settings but it must reappear if the user
                    // navigates back to a locked dashboard page.
                    onClick={() => router.push("/dashboard/settings")}
                  >
                    Go to settings
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
